import { useSwitchTeam } from "hooks/useSwitchTeam";
import { useRef, useState, useEffect } from "react";
import ReactGA from "react-ga4";
import { useIntl } from "react-intl";

import {
  ISendMessageBody,
  getNewChatTopic,
  IUser,
  IChat,
  AddHistoryMessage,
  setAdminAccountDeleted,
  logoutDispatch,
  setMsgStartGenerating,
  // setCreditLimitModal,
  setRememberChatTopicLoad,
} from "redux/actions";
import { useSelector } from "redux/hooks";
import { useAppNotification } from "../AppNotification";

// Import the EventType enum and handlers
import { EventType, isValidEventType } from "./EventTypes";
import * as EventHandlers from "./EventHandler";
import { removeQuotes } from "utils/functions";

export const useChatStream = () => {
  const userDetail = useSelector((state) => state.authReducer.userDetail);
  const addHistory = useSelector((state) => state.chatReducer.addHistory);
  // const { creditLimitModal } = useSelector((state) => state.chatReducer);
  const { formatMessage } = useIntl();
  let hasError = false;
  let notSupported = false;

  // Ref to hold the latest value of addHistory
  const addHistoryRef = useRef(addHistory);

  const switchTeam = useSwitchTeam();
  const { triggerNotification } = useAppNotification();

  useEffect(() => {
    // Update the ref whenever addHistory changes
    addHistoryRef.current = addHistory;
  }, [addHistory]);

  let controllerRef = useRef<AbortController | null>(null);
  const [done, setDone] = useState<boolean | null>(null);
  const [chatHistoryPagination, setChatHistory] = useState<IChat[]>([]);
  let chatId = useRef<number>(0);

  // Direct text extraction functions instead of regex
  const extractCompletedContent = (text: string): string | null => {
    // Find the completed event
    const eventStart = text.indexOf("event: completed");
    if (eventStart === -1) return null;

    // Find the data marker after the completed event
    const dataMarker = text.indexOf("data:", eventStart);
    if (dataMarker === -1) return null;

    // Extract everything after the data marker
    return text.substring(dataMarker + 5).trim();
  };

  // Direct text extraction functions instead of regex
  /**
   * Extracts content for a specific event type from an SSE stream buffer
   * @param text The SSE stream buffer text
   * @param eventName The name of the event to extract (e.g., 'citations', 'completed')
   * @returns The data content of the event or null if not found
   */
  const extractEventContent = (
    text: string,
    eventName: string
  ): string | null => {
    // Create the exact event marker to find
    const eventMarker = `event: ${eventName}`;

    // Find the starting position of this event
    const eventStart = text.indexOf(eventMarker);
    if (eventStart === -1) return null; // Event not found

    // Find the data marker that follows this event
    const dataMarker = text.indexOf("data:", eventStart);
    if (dataMarker === -1) return null; // No data for this event

    // Calculate the starting position of the actual data content
    const contentStart = dataMarker + 5; // 'data:' is 5 characters

    // Now find where this event's data ends

    // Option 1: Find the next event marker after this one
    const nextEventPos = text.indexOf("event:", contentStart);

    // Option 2: Find double newline which often separates events
    const doubleNewlinePos = text.indexOf("\n\n", contentStart);
    const crlfDoubleNewlinePos = text.indexOf("\r\n\r\n", contentStart);

    // Calculate the position where this event's data ends
    let contentEnd: number;

    if (nextEventPos !== -1) {
      // If there's another event, that's the end
      contentEnd = nextEventPos;
    } else if (doubleNewlinePos !== -1) {
      // If there's a double newline, that's the end
      contentEnd = doubleNewlinePos;
    } else if (crlfDoubleNewlinePos !== -1) {
      // If there's a CRLF double newline, that's the end
      contentEnd = crlfDoubleNewlinePos;
    } else {
      // No clear end marker, use the rest of the text
      contentEnd = text.length;
    }

    // Extract the content between start and end positions
    let content = text.substring(contentStart, contentEnd).trim();

    // Handle the case where content contains a newline (often happens with SSE)
    const newlinePos = content.indexOf("\n");
    if (newlinePos !== -1) {
      content = content.substring(0, newlinePos).trim();
    }

    return content;
  };

  // Function to process event-based stream data using enum and switch
  const processEventStream = (
    eventData: string,
    data: ISendMessageBody,
    admin_team_deleted: boolean,
    isPrivateChat: { current: boolean },
    setAdminTeamDeleted: (value: boolean) => void
  ) => {
    // Extract and process all important events, not just completed
    const citationsContent = extractEventContent(eventData, "citations");
    if (citationsContent) {
      try {
        // Parse the citations JSON array
        const citations = JSON.parse(citationsContent);
        // Call your handler with the parsed data
        EventHandlers.handleCitition(citations, data);
      } catch (e) {
        console.error("Failed to parse citations:", e);
      }
      // Note: NO return here - continue processing other events
    }
    // Try direct extraction of completed event first - most reliable
    const completedContent = extractCompletedContent(eventData);
    if (completedContent) {
      EventHandlers.handleCompleted(completedContent, data, admin_team_deleted);
      return;
    }

    // Regular event processing for non-completed events
    const events = eventData.split("event:").filter((event) => event.trim());
    for (const event of events) {
      const parts = event.trim().split("data:");
      if (parts.length < 2) continue;

      const eventTypeStr = parts[0].trim();
      const eventContent = parts[1].trim();

      // Skip if not a valid event type
      if (!isValidEventType(eventTypeStr)) continue;

      // Check for team not found in any event
      try {
        const contentObj = JSON.parse(eventContent);
        if (contentObj.error === "team not found") {
          setAdminTeamDeleted(true);
          continue;
        }
      } catch (e) {
        // Not JSON, continue with normal processing
      }

      // Cast to enum type for type-safe handling
      const eventType = eventTypeStr as EventType;

      // Use a switch statement with the enum for type-safe handling
      switch (eventType) {
        case EventType.FILES_UPLOADED:
          EventHandlers.handleFilesUploaded(eventContent);
          break;

        case EventType.CHAT_UPDATED:
          EventHandlers.handleChatUpdated(eventContent, isPrivateChat, chatId);
          break;

        case EventType.ASSISTANT_MESSAGE_CREATED:
          EventHandlers.handleAssistantMessageCreated(eventContent, data);
          break;

        case EventType.CREDITS_UPDATED:
          EventHandlers.handleCreditsUpdated(eventContent, userDetail ?? null);
          break;

        case EventType.IN_PROGRESS:
          EventHandlers.handleInProgress(eventContent, data);
          break;

        case EventType.ERROR:
          hasError = true;
          if (removeQuotes(eventContent) === "model not supported") {
            notSupported = true;
            break;
          }
          EventHandlers.handleErrorMessage(eventContent, data);
          break;

        case EventType.USER_MESSAGE_CREATED:
          EventHandlers.handleUserMessageCreated(eventContent, data);
          break;

        case EventType.MESSAGE:
          EventHandlers.handleMessage(eventContent, data, admin_team_deleted);
          break;

        case EventType.COMPLETED:
          // This should rarely be hit with our direct extraction approach
          EventHandlers.handleCompleted(eventContent, data, admin_team_deleted);
          break;

        case EventType.INCOMPLETE:
          EventHandlers.handleInComplete(eventContent, data);
          break;

        case EventType.CHAT_STARTED:
          EventHandlers.handleChatStarted(eventContent);
          break;

        case EventType.THINKING:
          EventHandlers.handleThinking(eventContent, data, admin_team_deleted);
          break;

        default:
          console.log("Unhandled event type:", eventType, eventContent);
      }
    }
  };

  // Helper function to try to read more data from the stream
  const tryReadMoreData = async (
    reader: ReadableStreamDefaultReader<Uint8Array> | undefined,
    decoder: TextDecoder,
    currentBuffer: string
  ): Promise<string> => {
    if (!reader) {
      return currentBuffer;
    }

    try {
      const extraResult = await reader.read();
      if (extraResult?.value) {
        const extraChunk = decoder.decode(extraResult.value, { stream: true });
        const newBuffer = currentBuffer + extraChunk;
        return newBuffer;
      }
    } catch (e) {
      console.log("Error reading additional data:", e);
    }
    return currentBuffer;
  };

  const startStream = (data: ISendMessageBody) => {
    return new Promise(async (resolve, reject) => {
      if (data.chatType === "document") {
        ReactGA.event({
          action: "Chat_DocumentMessage_Submitted",
          category: "WebsiteMessageSentDocumentChat",
        });
      }
      try {
        controllerRef.current = new AbortController();

        // let body: any;
        // if (data.file_path && data.file_path.length > 0) {
        //   body = {
        //     message: data.message,
        //     newChatBoolean: data.newChatBoolean,
        //     chatId: data.chatId,
        //     model: data.model,
        //     // chatType: data.chatType,
        //     file_path: data.file_path,
        //     images: data.images,
        //     regenerate: data.regenerate,
        //     isPrivate: data.isPrivate,
        //     folder_id: data.folder_id,
        //   };
        // } else if (data.file_path && data.file_path.length > 0) {
        //   body = {
        //     message: data.message,
        //     newChatBoolean: data.newChatBoolean,
        //     chatId: data.chatId,
        //     model: data.model,
        //     // chatType: data.chatType,
        //     file_path: data.file_path,
        //     regenerate: data.regenerate,
        //     isPrivate: data.isPrivate,
        //     folder_id: data.folder_id,
        //   };
        // } else if (data.images) {
        //   body = {
        //     message: data.message,
        //     newChatBoolean: data.newChatBoolean,
        //     chatId: data.chatId,
        //     model: data.model,
        //     // chatType: data.chatType,
        //     images: data.images,
        //     regenerate: data.regenerate,
        //     isPrivate: data.isPrivate,
        //     folder_id: data.folder_id,
        //   };
        // } else {
          let body:any = {
            message: data.message,
            newChatBoolean: data.newChatBoolean,
            chatId: data.chatId,
            model: data.model,
            // chatType: data.chatType,
            regenerate: data.regenerate,
            isPrivate: data.isPrivate,
            folder_id: data.folder_id,
          };
        // }

        if(data.file_path && data.file_path.length > 0){
          body.file_path = data.file_path
        }

        if(data.images && data.images.length > 0){
          body.images = data.images
        }

        const url = new URL(
          `${process.env.REACT_APP_API_BASEURL}/api/chat/message`
        );

        // Append team_id as a query parameter
        url.searchParams.append(
          "team_id",
          userDetail?.user?.team?.id.toString() ?? ""
        );

        const response = await fetch(url, {
          method: "POST",
          headers: {
            Accept: "text/event-stream",
            "Content-Type": "application/json",
            Authorization: `Bearer ${userDetail?.token}`,
          },
          body: JSON.stringify(body),
          signal: controllerRef?.current.signal,
        });

        if (response?.status === 403) {
          logoutDispatch();
          triggerNotification({
            message: formatMessage({ id: "account.deleted.message" }),
            type: "error",
          });
          return;
        }

        // 🔥 Add this BEFORE trying to read from the stream
        if (response.status === 422) {
          const json = await response.json();
          triggerNotification({
            message: json.message || "An error occurred. Please try again.",
            type: "error",
          });
          reject(json.message);
          return;
        }

        const reader = response?.body?.getReader();
        const decoder = new TextDecoder();

        setDone(false);
        setMsgStartGenerating(true);
        let streamBuffer = "";
        const isPrivateChat = { current: false }; // Use a reference object
        let admin_team_deleted = false;
        let InsufficientCredits = false;

        // Function to set admin_team_deleted flag
        const setAdminTeamDeleted = (value: boolean) => {
          admin_team_deleted = value;
        };

        // Track if we're collecting a completed event
        let collectingCompleted = false;
        let completedTimeout: ReturnType<typeof setTimeout> | null = null;
        let completedReadAttempts = 0;
        const MAX_COMPLETED_READ_ATTEMPTS = 5; // Maximum number of attempts to read more data

        while (true) {
          const streamResult = await reader?.read();

          if (!streamResult?.value) {
            if (streamResult?.done) {
              setDone(true);
              controllerRef.current = null;

              // Process any remaining buffer content
              if (streamBuffer.includes("event: completed")) {
                // One final attempt to get any remaining data
                streamBuffer = await tryReadMoreData(
                  reader,
                  decoder,
                  streamBuffer
                );

                // Process whatever we have
                processEventStream(
                  streamBuffer,
                  data,
                  admin_team_deleted,
                  isPrivateChat,
                  setAdminTeamDeleted
                );
              }

              if (streamBuffer.includes("event: error")) {
                streamBuffer = await tryReadMoreData(
                  reader,
                  decoder,
                  streamBuffer
                );
                processEventStream(
                  streamBuffer,
                  data,
                  admin_team_deleted,
                  isPrivateChat,
                  setAdminTeamDeleted
                );
              }

              if (streamBuffer.includes("event: citations")) {
                streamBuffer = await tryReadMoreData(
                  reader,
                  decoder,
                  streamBuffer
                );
                processEventStream(
                  streamBuffer,
                  data,
                  admin_team_deleted,
                  isPrivateChat,
                  setAdminTeamDeleted
                );
              }

              // Handle chat history update logic
              if (addHistoryRef.current === "") {
                setTimeout(() => {
                  if (
                    data.newChatBoolean &&
                    chatId.current &&
                    isPrivateChat.current === false
                  ) {
                    const ID = chatId.current;
                    setRememberChatTopicLoad(true);
                    getNewChatTopic(chatId.current, data.folder_id)
                      .then(() => {
                        setTimeout(() => AddHistoryMessage(ID), 100);
                      })
                      .catch((err: any) => console.error(err));

                    setTimeout(() => (chatId.current = 0), 110);
                  }
                }, 2000);
              }

              if (admin_team_deleted) {
                setAdminAccountDeleted(true);
                switchTeam();
                reject("Team has been deleted by admin");
              }

              if (InsufficientCredits) {
                reject("Insufficient credits");
              }
              if (notSupported) {
                reject("model not supported");
              }

              resolve({
                ...streamResult,
                chatId: hasError ? null : chatId.current,
              });
              break;
            }
            continue;
          }

          const decodedChunk = decoder.decode(streamResult.value, {
            stream: true,
          });
          streamBuffer += decodedChunk;

          // If we detect a completed event, enter collection mode
          if (
            streamBuffer.includes("event: completed") &&
            !collectingCompleted
          ) {
            collectingCompleted = true;
            // completedDetectedAt = Date.now();
            completedReadAttempts = 0;

            // Clear any existing timeout
            if (completedTimeout) {
              clearTimeout(completedTimeout);
            }

            // Set a longer timeout to collect as much data as possible
            completedTimeout = setTimeout(async () => {
              // Make several attempts to read more data with progressively longer timeouts
              const readMoreData = async () => {
                if (completedReadAttempts >= MAX_COMPLETED_READ_ATTEMPTS) {
                  return;
                }

                completedReadAttempts++;

                // Try to read more data
                streamBuffer = await tryReadMoreData(
                  reader,
                  decoder,
                  streamBuffer
                );

                // Wait a bit longer each time
                const delayTime = 50 * completedReadAttempts;

                await new Promise((r) => setTimeout(r, delayTime));
                await readMoreData();
              };

              // Start the recursive read attempts
              await readMoreData();

              // Generate a direct raw message for the completed event with just the content
              // This bypasses any potential issues with event parsing
              const rawCompletedContent = extractCompletedContent(streamBuffer);
              if (rawCompletedContent) {
                // Directly call handleCompleted with the raw content
                EventHandlers.handleCompleted(
                  rawCompletedContent,
                  data,
                  admin_team_deleted
                );
              } else {
                processEventStream(
                  streamBuffer,
                  data,
                  admin_team_deleted,
                  isPrivateChat,
                  setAdminTeamDeleted
                );
              }

              // Reset state
              streamBuffer = "";
              collectingCompleted = false;
            }, 300); // Longer delay (300ms) to ensure we get everything

            // Skip normal processing while we collect the completed event
            continue;
          }

          // If we're in collection mode, wait for the timeout to process
          if (collectingCompleted) {
            continue;
          }

          // Regular event processing for non-completed events
          if (
            streamBuffer.includes("event:") &&
            !streamBuffer.includes("event: completed")
          ) {
            // Process complete events with double newlines
            let eventEndPattern = /event:.*?\r?\n\r?\n/g;
            let match;
            let processedBuffer = "";
            let lastIndex = 0;

            // Regex to match complete events
            while ((match = eventEndPattern.exec(streamBuffer)) !== null) {
              const completeEvent = match[0];
              processedBuffer += completeEvent;
              lastIndex = match.index + completeEvent.length;
            }

            if (processedBuffer.length > 0) {
              // Process the complete events
              processEventStream(
                processedBuffer,
                data,
                admin_team_deleted,
                isPrivateChat,
                setAdminTeamDeleted
              );

              // Keep only the unprocessed part in the buffer
              streamBuffer = streamBuffer.substring(lastIndex);
            }

            // Alternative approach for events without double newlines
            if (streamBuffer.includes("event:")) {
              const processableEvents = [];
              let currentPosition = 0;

              while (true) {
                const eventStart = streamBuffer.indexOf(
                  "event:",
                  currentPosition
                );
                if (eventStart === -1) break;

                const nextEventStart = streamBuffer.indexOf(
                  "event:",
                  eventStart + 6
                );

                if (nextEventStart !== -1) {
                  // We found a complete event
                  processableEvents.push(
                    streamBuffer.substring(eventStart, nextEventStart)
                  );
                  currentPosition = nextEventStart;
                } else {
                  // This might be an incomplete event, keep it in the buffer
                  break;
                }
              }

              // Process each complete event
              if (processableEvents.length > 0) {
                const eventsToProcess = processableEvents.join("");
                processEventStream(
                  eventsToProcess,
                  data,
                  admin_team_deleted,
                  isPrivateChat,
                  setAdminTeamDeleted
                );

                // Remove processed events from buffer
                streamBuffer = streamBuffer.substring(currentPosition);
              }
            }
          } else if (!streamBuffer.includes("event: completed")) {
            // Check for team not found in non-event format
            try {
              const jsonData = JSON.parse(streamBuffer);
              if (jsonData.message === "Team not found") {
                setAdminTeamDeleted(true);
                streamBuffer = "";
              } else if (jsonData.errors.message === "Insufficient credits") {
                InsufficientCredits = true;
                streamBuffer = "";
              }
            } catch (e) {
              // Not valid JSON or we need to wait for more data
            }
          }
        }

        // Analytics events
        ReactGA.event({
          action: "Chat_Response_Success",
          category: (userDetail?.user as IUser).activeSubscription.name,
          label: new Date().toISOString(),
          value: 5,
        });

        ReactGA.event({
          action: "Credits_Usage_Tracked",
          category: "WebsiteChatScreenCreditUsed_category",
          label: new Date().toISOString(),
        });
      } catch (error) {
        console.error("Stream error:", error);
        reject(error);
      }
    });
  };

  return {
    done,
    setDone,
    startStream,
    controllerRef,
    chatHistoryPagination,
    setChatHistory,
  };
};
